import { useMemo, useState } from 'react';
import { useAuth } from '../../lib/auth/AuthContext';
import { COL, orderBy } from '../../lib/collections';
import { useLiveQuery } from '../../lib/data/hooks';
import { ROLE_META } from '../../lib/constants';
import { Avatar } from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Field';
import { BottomSheet } from '../../components/ui/BottomSheet';
import { LoadingBlock, EmptyState } from '../../components/ui/States';
import { ArrowLeft, Eye, Search } from 'lucide-react';
import type { Role, UserDoc } from '../../lib/types';

const IMPERSONATABLE_ROLES: Role[] = ['mahasiswa', 'dosen', 'staf_jurusan', 'staf_fakultas'];

/** Full-screen picker for admins to preview any active user's interface per role. */
export function ImpersonateSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { startImpersonation, stopImpersonation, isImpersonating, impersonatedUser } = useAuth();
  const [roleFilter, setRoleFilter] = useState<Role | 'semua'>('semua');
  const [query, setQuery] = useState('');

  const { data, loading } = useLiveQuery<UserDoc>(
    open ? COL.users : null,
    [orderBy('name', 'asc')],
    open,
  );

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.filter((u) => {
      if (u.status !== 'active') return false;
      if (roleFilter !== 'semua' && u.role !== roleFilter) return false;
      if (
        q &&
        !(
          u.name.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q) ||
          u.uid.toLowerCase().includes(q)
        )
      )
        return false;
      return true;
    });
  }, [data, roleFilter, query]);

  return (
    <BottomSheet open={open} onClose={onClose} title="Pratinjau peran" height="full">
      <div className="space-y-4">
        {isImpersonating && impersonatedUser && (
          <div className="rounded-none bg-accent-soft p-4">
            <div className="flex items-center gap-3">
              <Avatar name={impersonatedUser.name} size="md" />
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-muted">Menampilkan sebagai</div>
                <div className="truncate text-[15px] font-bold text-ink">
                  {impersonatedUser.name}
                </div>
              </div>
              <Button size="sm" variant="soft" onClick={stopImpersonation}>
                <ArrowLeft className="size-4" /> Kembali
              </Button>
            </div>
          </div>
        )}

        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-faint" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cari nama, email, atau UID…"
            className="pl-11"
            aria-label="Cari pengguna"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setRoleFilter('semua')}
            className={`rounded-none px-4 py-2 text-[13px] font-semibold press ${roleFilter === 'semua' ? 'bg-accent text-white' : 'bg-surface-2 text-muted'}`}
          >
            Semua peran
          </button>
          {IMPERSONATABLE_ROLES.map((role) => (
            <button
              key={role}
              onClick={() => setRoleFilter(role)}
              className={`rounded-none px-4 py-2 text-[13px] font-semibold press ${roleFilter === role ? 'bg-accent text-white' : 'bg-surface-2 text-muted'}`}
            >
              {ROLE_META[role].label}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingBlock rows={5} />
        ) : list.length === 0 ? (
          <EmptyState
            title="Tidak ada pengguna"
            body="Tidak ada akun aktif dengan kriteria tersebut."
          />
        ) : (
          <div className="card overflow-hidden">
            {list.map((u, i) => (
              <div key={u.id}>
                {i > 0 && <div className="mx-4 h-px bg-hairline" />}
                <button
                  onClick={() => {
                    startImpersonation(u.id);
                    onClose();
                  }}
                  className="flex w-full items-center gap-3 px-4 py-3 text-left press"
                >
                  <Avatar name={u.name} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] font-semibold text-ink">{u.name}</div>
                    <div className="truncate text-[12px] text-muted">{u.email || u.uid}</div>
                  </div>
                  <Badge tone="accent">{ROLE_META[u.role].label}</Badge>
                  <span className="grid size-9 shrink-0 place-items-center rounded-none bg-surface-2 text-accent">
                    <Eye className="size-4" />
                  </span>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
